import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

import {
  parseStreetAddress,
  parseStreetName,
  STREET_NAME_MAX_LENGTH,
  STREET_NAME_PATTERN,
} from './parse-street-address';

export { parseStreetName, STREET_NAME_MAX_LENGTH };

/**
 * Matches a trailing 2-letter state abbreviation followed by a 5-digit ZIP
 * (optionally a ZIP+4), e.g. "123 Main St Akron OH 44301" — a strong signal
 * that a full address has been pasted/typed into the street line.
 */
const TRAILING_STATE_ZIP = /[A-Za-z]{2}\.?\s*,?\s*\d{5}(?:-\d{4})?\s*$/;

/**
 * Flags `addressLine1` when its value looks like a full address (street +
 * city/state/ZIP) rather than just the street line, or when the parsed street
 * name is missing or fails length/charset rules. Street name is required.
 * See SVRS-41581 and product street address rules.
 *
 * Full-address detection is intentionally conservative to avoid false-blocking
 * valid street lines:
 *  - a comma (full addresses separate the street from city/state/ZIP),
 *  - a trailing `<state> <ZIP>` token, or
 *  - duplication of the city / state already captured in their own fields.
 *
 * No value is silently rewritten; the field is simply marked invalid so the
 * existing inline error markup can prompt the user to enter the street only.
 */
export function residenceAddressContaminationValidator(): ValidatorFn {
  return (addressControl: AbstractControl): ValidationErrors | null => {
    const value = (addressControl.value ?? '').toString().trim();
    if (!value) {
      return null;
    }

    if (value.includes(',')) {
      return { streetNameError: true };
    }

    if (TRAILING_STATE_ZIP.test(value)) {
      return { streetNameError: true };
    }

    const parent = addressControl.parent;
    if (parent) {
      const normalizedValue = value.toLowerCase();
      const city = (parent.get('city')?.value ?? '').toString().trim();
      const state = (parent.get('userState')?.value ?? '').toString().trim();

      if (city && normalizedValue.includes(city.toLowerCase())) {
        return { streetNameError: true };
      }

      if (
        /^[A-Za-z]{2}$/.test(state) &&
        new RegExp(`\\b${state}\\b\\s*$`, 'i').test(value)
      ) {
        return { streetNameError: true };
      }
    }

    const { streetName } = parseStreetAddress(value);

    // Street name is required once a non-empty address line is present.
    if (!streetName) {
      return { streetNameError: true };
    }

    if (
      streetName.length > STREET_NAME_MAX_LENGTH ||
      !STREET_NAME_PATTERN.test(streetName)
    ) {
      return { streetNameError: true };
    }

    return null;
  };
}
